import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AuthAPIError,
  authClient,
  createPasskeyCredential,
  isPasswordSessionResponse,
  isWebAuthnAvailable,
  type AuthClient,
  type AuthMeResponse,
  type AuthPolicy,
  type AuthSession,
  type AuthUser,
  type EnrollPasskeyOptions,
  type Passkey,
  type PasskeyRegistrationResult,
  type PasswordLoginResponse,
  type SessionTokenResponse,
  type StepUpMethod,
} from "./api";
import { passkeyFailure, PASSKEY_SIGN_IN_WORDS } from "./passkeyFailure";
import {
  isStepUpCancelled,
  SSO_STEP_UP_FEATURES,
  SSO_STEP_UP_WINDOW,
  waitForSSOStepUp,
} from "./ssoStepUp";

const refreshTokenStorageKey = "starling.refresh_token";
const DEFAULT_STEP_UP_METHODS: readonly StepUpMethod[] = ["totp"];
const invalidTOTPMessage = "Enter the six digits your authenticator app shows.";

export type AuthStatus =
  | "loading"
  | "anonymous"
  | "authenticated"
  | "session_expired";

interface PendingChallenge {
  email: string;
  totpChallengeToken: string;
  recoveryChallengeToken: string;
  expiresAt: string;
  /** The challenges of this attempt the server has spent. It spends one on
   * its first try, right or wrong, so a spent challenge cannot be retried;
   * the other may still be used. */
  spent: Record<ChallengeMethod, boolean>;
}

export type ChallengeMethod = "totp" | "recovery";

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  session: AuthSession | null;
  featureFlags: Record<string, boolean> | null;
  /** Per-user authorization facts from /auth/me (null until loaded). */
  capabilities: Record<string, boolean> | null;
  /** How this person can confirm a privileged act right now. */
  stepUpMethods: readonly StepUpMethod[];
  policy: AuthPolicy | null;
  pendingChallenge: PendingChallenge | null;
  accessToken: string | null;
  webAuthnAvailable: boolean;
  error: string | null;
  clearError(): void;
  signInWithPasskey(): Promise<void>;
  startPasswordLogin(
    email: string,
    password: string,
  ): Promise<PasswordLoginResponse>;
  completeTOTP(code: string): Promise<void>;
  completeRecovery(code: string): Promise<void>;
  /** Drops the pending sign-in attempt and its error. */
  abandonChallenge(): void;
  completeSSOExchange(exchangeCode: string): Promise<void>;
  establishSession(tokens: SessionTokenResponse): Promise<void>;
  completeStepUp(code: string): Promise<void>;
  /** Confirms a privileged act by signing in again with single sign-on, in
   * a window of its own. Call it straight from a click: the window opens
   * before anything is awaited, or the browser blocks it. Resolves once the
   * server has stepped this session up; `signal` cancels it (and closes the
   * window). One attempt runs at a time: a new one cancels the last. */
  confirmWithSSO(signal?: AbortSignal): Promise<void>;
  refreshSession(): Promise<void>;
  reloadCurrentUser(): Promise<AuthMeResponse>;
  refreshFeatureFlags(): Promise<void>;
  withFreshAccessToken<T>(operation: (token: string) => Promise<T>): Promise<T>;
  logout(): Promise<void>;
  logoutAll(): Promise<void>;
  /** `recoveryCode` confirms the passkey that finishes a recovery when the
   * session is no longer fresh; a refused code throws RECOVERY_CODE_REFUSED. */
  enrollPasskey(
    nickname?: string,
    options?: EnrollPasskeyOptions,
  ): Promise<PasskeyRegistrationResult>;
  listPasskeys(): Promise<Passkey[]>;
  renamePasskey(id: string, nickname: string): Promise<Passkey>;
  deletePasskey(id: string): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  client?: AuthClient;
  createCredential?: (options: unknown) => Promise<unknown>;
  children: ReactNode;
}

export function AuthProvider({
  client = authClient,
  createCredential = createPasskeyCredential,
  children,
}: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [featureFlags, setFeatureFlags] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [capabilities, setCapabilities] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [stepUpMethods, setStepUpMethods] = useState<readonly StepUpMethod[]>(
    DEFAULT_STEP_UP_METHODS,
  );
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [policy, setPolicy] = useState<AuthPolicy | null>(null);
  const [pendingChallenge, setPendingChallenge] =
    useState<PendingChallenge | null>(null);
  // The attempt a code is being checked against. A response that arrives for
  // an attempt the person already left must not end or explain the next one.
  const liveChallengeRef = useRef<PendingChallenge | null>(null);
  useEffect(() => {
    liveChallengeRef.current = pendingChallenge;
  }, [pendingChallenge]);
  const [error, setError] = useState<string | null>(null);
  const accessTokenRef = useRef<string | null>(null);
  const ssoAttemptRef = useRef<AbortController | null>(null);
  const refreshInFlightRef = useRef<Promise<string> | null>(null);
  const initialRefreshInFlightRef =
    useRef<Promise<SessionTokenResponse> | null>(null);

  const setActiveAccessToken = useCallback((token: string | null) => {
    accessTokenRef.current = token;
    setAccessToken(token);
  }, []);

  // Four readers write the flag snapshot — establishing a session, reloading the
  // user, re-reading the flags alone, and signing out — and they run on
  // independent lifecycles, so more than one can be in flight at once: a console
  // that re-reads on every landed turn and on every return to the tab is one
  // slow response away from two. Responses are not ordered, so the last one to
  // arrive is not the last one asked, and without a ticket the older answer wins
  // by arriving second: a control the operator has since restored stays
  // withheld, or one they have since retired is offered again — the
  // enabled-and-refused state EC-3 forbids — until something happens to
  // re-read. So each read takes a ticket before it asks and writes only if no
  // later read has already landed. Signing out takes a ticket too, which is what
  // stops a read issued before it from restoring the flags of a session that has
  // ended.
  const featureFlagReadRef = useRef(0);
  const landedFeatureFlagReadRef = useRef(0);
  const claimFeatureFlagRead = useCallback(() => {
    featureFlagReadRef.current += 1;
    return featureFlagReadRef.current;
  }, []);
  const applyFeatureFlags = useCallback(
    (read: number, next: Record<string, boolean> | null) => {
      if (read < landedFeatureFlagReadRef.current) return;
      landedFeatureFlagReadRef.current = read;
      setFeatureFlags((existing) =>
        sameFeatureFlags(existing, next) ? existing : next,
      );
    },
    [],
  );

  const clearState = useCallback(
    (nextStatus: AuthStatus = "anonymous") => {
      setError(null);
      setActiveAccessToken(null);
      setUser(null);
      setSession(null);
      setStepUpMethods(DEFAULT_STEP_UP_METHODS);
      applyFeatureFlags(claimFeatureFlagRead(), null);
      setPendingChallenge(null);
      clearStoredRefreshToken();
      setStatus(nextStatus);
    },
    [applyFeatureFlags, claimFeatureFlagRead, setActiveAccessToken],
  );

  const loadPolicy = useCallback(async () => {
    try {
      setPolicy(await client.policy());
    } catch {
      setPolicy({ registration_open: false });
    }
  }, [client]);

  const expireSession = useCallback(() => {
    clearState("session_expired");
    setError("Your session expired. Sign in again.");
  }, [clearState]);

  const establishSession = useCallback(
    async (tokens: SessionTokenResponse) => {
      setActiveAccessToken(tokens.access_token);
      writeStoredRefreshToken(tokens.refresh_token);
      const read = claimFeatureFlagRead();
      const current = await client.me(tokens.access_token);
      setUser(current.user);
      setSession(current.session);
      applyFeatureFlags(read, current.feature_flags ?? null);
      setCapabilities(current.capabilities ?? null);
      setStepUpMethods(current.step_up_methods ?? DEFAULT_STEP_UP_METHODS);
      setPendingChallenge(null);
      setStatus("authenticated");
    },
    [applyFeatureFlags, claimFeatureFlagRead, client, setActiveAccessToken],
  );

  const refreshAccessToken = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }
    const refreshToken = readStoredRefreshToken();
    if (!refreshToken) {
      expireSession();
      throw new AuthAPIError("Unauthorized", 401, "UNAUTHORIZED");
    }

    const refresh = (async () => {
      try {
        const tokens = await client.refresh(refreshToken);
        await establishSession(tokens);
        return tokens.access_token;
      } catch (err) {
        // A refused token ends the session; a network failure or a server
        // fault is an outage the session outlives (EC-6).
        if (isUnauthorizedError(err)) expireSession();
        throw err;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();
    refreshInFlightRef.current = refresh;
    return refresh;
  }, [client, establishSession, expireSession]);

  const expireIfUnauthorized = useCallback(
    (err: unknown) => {
      if (isUnauthorizedError(err)) {
        expireSession();
        return true;
      }
      return false;
    },
    [expireSession],
  );

  const refreshSession = useCallback(async () => {
    if (!readStoredRefreshToken()) {
      clearState("anonymous");
      return;
    }
    await refreshAccessToken();
  }, [clearState, refreshAccessToken]);

  const getInitialRefresh = useCallback(() => {
    const refreshToken = readStoredRefreshToken();
    if (!refreshToken) {
      return null;
    }
    if (!initialRefreshInFlightRef.current) {
      initialRefreshInFlightRef.current = client
        .refresh(refreshToken)
        .finally(() => {
          initialRefreshInFlightRef.current = null;
        });
    }
    return initialRefreshInFlightRef.current;
  }, [client]);

  const withFreshAccessToken = useCallback(
    async <T,>(operation: (token: string) => Promise<T>) => {
      const token = accessTokenRef.current;
      if (!token) {
        throw new Error("No active access token");
      }
      try {
        return await operation(token);
      } catch (err) {
        if (!isUnauthorizedError(err)) {
          throw err;
        }
        const refreshedToken = await refreshAccessToken();
        try {
          return await operation(refreshedToken);
        } catch (retryErr) {
          expireIfUnauthorized(retryErr);
          throw retryErr;
        }
      }
    },
    [expireIfUnauthorized, refreshAccessToken],
  );

  useEffect(() => {
    let active = true;
    void loadPolicy();
    const initialRefresh = getInitialRefresh();
    if (!initialRefresh) {
      setStatus("anonymous");
      return () => {
        active = false;
      };
    }
    initialRefresh
      .then(async (tokens) => {
        if (active) {
          await establishSession(tokens);
        }
      })
      .catch(() => {
        if (active) {
          expireSession();
        }
      });
    return () => {
      active = false;
    };
  }, [establishSession, expireSession, getInitialRefresh, loadPolicy]);

  const reloadCurrentUser = useCallback(async () => {
    const read = claimFeatureFlagRead();
    const current = await withFreshAccessToken((token) => client.me(token));
    setUser(current.user);
    setSession(current.session);
    applyFeatureFlags(read, current.feature_flags ?? null);
    setCapabilities(current.capabilities ?? null);
    setStepUpMethods(current.step_up_methods ?? DEFAULT_STEP_UP_METHODS);
    setStatus("authenticated");
    return current;
  }, [applyFeatureFlags, claimFeatureFlagRead, client, withFreshAccessToken]);

  /**
   * Re-read this session's feature flags, disturbing nothing else it holds.
   *
   * The snapshot is handed out when the session is established, so a console
   * left open across a rollout keeps answering from the flags it was born with:
   * a surface that must withhold a control the server has since retired — or
   * offer one a rollback restored — can only learn that by asking again.
   * Callers put this on their own read lifecycle. It writes state only when a
   * value actually moved, so a call that finds nothing new costs one request
   * and no re-render — and only when no later read has already landed, so two
   * calls in flight resolve to the newer one whichever order they come back in.
   */
  const refreshFeatureFlags = useCallback(async () => {
    const read = claimFeatureFlagRead();
    const current = await withFreshAccessToken((token) => client.me(token));
    applyFeatureFlags(read, current.feature_flags ?? null);
    setCapabilities(current.capabilities ?? null);
    setStepUpMethods(current.step_up_methods ?? DEFAULT_STEP_UP_METHODS);
  }, [applyFeatureFlags, claimFeatureFlagRead, client, withFreshAccessToken]);

  const signInWithPasskey = useCallback(async () => {
    setError(null);
    try {
      await establishSession(await client.loginPasskey());
    } catch (err) {
      setError(PASSKEY_SIGN_IN_WORDS[passkeyFailure(err)]);
      throw err;
    }
  }, [client, establishSession]);

  const startPasswordLogin = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const challenge = await client.loginPassword(email, password);
        if (isPasswordSessionResponse(challenge)) {
          await establishSession(challenge.session);
          return challenge;
        }
        setPendingChallenge({
          email,
          totpChallengeToken: challenge.totp_challenge_token,
          recoveryChallengeToken: challenge.recovery_challenge_token,
          expiresAt: challenge.expires_at,
          spent: { totp: false, recovery: false },
        });
        return challenge;
      } catch (err) {
        setError(errorMessage(err));
        throw err;
      }
    },
    [client, establishSession],
  );

  const endChallenge = useCallback(
    (err: unknown, method: ChallengeMethod, token: string) => {
      const live = liveChallengeRef.current;
      if (!live || challengeToken(live, method) !== token) return;
      const status = statusOf(err);
      if (status === undefined || status === 422) {
        setError(
          status === undefined
            ? "Starling couldn't be reached. Check your connection and try again."
            : errorMessage(err),
        );
        return;
      }
      const expired = Date.parse(live.expiresAt) <= Date.now();
      const spent =
        status === 429 || expired
          ? { totp: true, recovery: true }
          : { ...live.spent, [method]: true };
      setPendingChallenge((current) =>
        current && challengeToken(current, method) === token
          ? { ...current, spent }
          : current,
      );
      setError(endedChallengeMessage(status, method, { ...live, spent }));
    },
    [],
  );

  const completeTOTP = useCallback(
    async (code: string) => {
      if (!pendingChallenge || pendingChallenge.spent.totp) {
        throw new Error("No TOTP challenge is active");
      }
      setError(null);
      if (!/^\d{6}$/.test(code)) {
        setError(invalidTOTPMessage);
        throw new AuthAPIError(invalidTOTPMessage, 422, "VALIDATION_FAILED");
      }
      const token = pendingChallenge.totpChallengeToken;
      try {
        await establishSession(await client.loginTOTP(token, code));
      } catch (err) {
        endChallenge(err, "totp", token);
        throw err;
      }
    },
    [client, endChallenge, establishSession, pendingChallenge],
  );

  const completeRecovery = useCallback(
    async (code: string) => {
      if (!pendingChallenge || pendingChallenge.spent.recovery) {
        throw new Error("No recovery challenge is active");
      }
      setError(null);
      const token = pendingChallenge.recoveryChallengeToken;
      try {
        await establishSession(await client.loginRecovery(token, code));
      } catch (err) {
        endChallenge(err, "recovery", token);
        throw err;
      }
    },
    [client, endChallenge, establishSession, pendingChallenge],
  );

  const abandonChallenge = useCallback(() => {
    liveChallengeRef.current = null;
    setPendingChallenge(null);
    setError(null);
  }, []);

  const completeSSOExchange = useCallback(
    async (exchangeCode: string) => {
      setError(null);
      try {
        await establishSession(await client.exchangeSSOCode(exchangeCode));
      } catch (err) {
        setError(errorMessage(err));
        throw err;
      }
    },
    [client, establishSession],
  );

  const completeStepUp = useCallback(
    async (code: string) => {
      setError(null);
      try {
        await withFreshAccessToken((token) =>
          client.stepUp(token, { method: "totp", totp: code }),
        );
        await reloadCurrentUser();
      } catch (err) {
        if (!expireIfUnauthorized(err)) {
          setError(errorMessage(err));
        }
        throw err;
      }
    },
    [client, expireIfUnauthorized, reloadCurrentUser, withFreshAccessToken],
  );

  const confirmWithSSO = useCallback(
    async (signal?: AbortSignal) => {
      setError(null);
      ssoAttemptRef.current?.abort();
      const popup = window.open("", SSO_STEP_UP_WINDOW, SSO_STEP_UP_FEATURES);
      if (popup === null) {
        throw new Error(
          "Your browser blocked the sign-in window. Allow pop-ups for Starling and try again.",
        );
      }
      const attempt = new AbortController();
      ssoAttemptRef.current = attempt;
      const cancel = () => attempt.abort();
      signal?.addEventListener("abort", cancel);
      if (signal?.aborted) attempt.abort();
      try {
        // The baseline is read from the server now, not this tab's memory:
        // another tab sharing the session may have stepped up since, and an
        // old step-up must not pass for this one.
        const current = await withFreshAccessToken((token) => client.me(token));
        const before = current.session.stepup_verified_at ?? null;
        const { redirect_url: redirectURL } = await withFreshAccessToken(
          (token) => client.startSSOStepUp(token),
        );
        if (attempt.signal.aborted) {
          throw new DOMException("Cancelled", "AbortError");
        }
        popup.location.href = redirectURL;
        await waitForSSOStepUp(popup, {
          signal: attempt.signal,
          confirmed: async () => {
            const current = await withFreshAccessToken((token) =>
              client.me(token),
            );
            return steppedUpSince(current.session.stepup_verified_at, before);
          },
        });
        await reloadCurrentUser();
        if (attempt.signal.aborted) {
          throw new DOMException("Cancelled", "AbortError");
        }
      } catch (err) {
        if (!popup.closed) popup.close();
        if (!isStepUpCancelled(err) && !expireIfUnauthorized(err)) {
          setError(errorMessage(err));
        }
        throw err;
      } finally {
        signal?.removeEventListener("abort", cancel);
        if (ssoAttemptRef.current === attempt) ssoAttemptRef.current = null;
      }
    },
    [client, expireIfUnauthorized, reloadCurrentUser, withFreshAccessToken],
  );

  const logout = useCallback(async () => {
    const token = accessToken;
    clearState("anonymous");
    if (token) {
      await client.logout(token).catch(() => undefined);
    }
  }, [accessToken, clearState, client]);

  const logoutAll = useCallback(async () => {
    const token = accessToken;
    clearState("anonymous");
    if (token) {
      await client.logoutAll(token).catch(() => undefined);
    }
  }, [accessToken, clearState, client]);

  // The caller says why adding a passkey stopped (passkeyFailure). Only a
  // session the server refused after a refresh ends it: a refused passkey
  // answers 422 and leaves the person signed in.
  const enrollPasskey = useCallback(
    async (nickname?: string, options?: EnrollPasskeyOptions) => {
      try {
        const recoveryCode = options?.recoveryCode;
        const challenge = await withFreshAccessToken((token) =>
          recoveryCode
            ? client.beginPasskeyRegistration(token, recoveryCode)
            : client.beginPasskeyRegistration(token),
        ).catch((err: unknown) => {
          // Begin answers 422 only for the recovery code; finish's 422 is a
          // refused passkey, so the two must not read alike.
          if (
            recoveryCode &&
            err instanceof AuthAPIError &&
            err.status === 422
          ) {
            throw new AuthAPIError(err.message, 422, "RECOVERY_CODE_REFUSED");
          }
          throw err;
        });
        options?.onDevicePrompt?.();
        const credential = await createCredential(challenge.options);
        const result = await withFreshAccessToken((token) =>
          client.finishPasskeyRegistration(
            token,
            challenge.challenge_token,
            credential,
            nickname,
          ),
        );
        if (result.recovery_required_cleared) {
          setSession((current) =>
            current ? { ...current, recovery_required: false } : current,
          );
        }
        try {
          await reloadCurrentUser();
        } catch (err) {
          // The passkey is saved either way; new recovery codes must reach
          // the screen even when the reload fails.
          if (!result.recovery_codes?.length && isUnauthorizedError(err)) {
            throw err;
          }
        }
        return result;
      } catch (err) {
        expireIfUnauthorized(err);
        throw err;
      }
    },
    [
      client,
      createCredential,
      expireIfUnauthorized,
      reloadCurrentUser,
      withFreshAccessToken,
    ],
  );

  const listPasskeys = useCallback(async () => {
    return withFreshAccessToken((token) => client.listPasskeys(token));
  }, [client, withFreshAccessToken]);

  const renamePasskey = useCallback(
    async (id: string, nickname: string) =>
      withFreshAccessToken((token) =>
        client.renamePasskey(token, id, nickname),
      ),
    [client, withFreshAccessToken],
  );

  const deletePasskey = useCallback(
    async (id: string) => {
      setError(null);
      await withFreshAccessToken((token) => client.deletePasskey(token, id));
    },
    [client, withFreshAccessToken],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      session,
      featureFlags,
      capabilities,
      stepUpMethods,
      policy,
      pendingChallenge,
      accessToken,
      webAuthnAvailable: isWebAuthnAvailable(),
      error,
      clearError: () => setError(null),
      signInWithPasskey,
      startPasswordLogin,
      completeTOTP,
      completeRecovery,
      abandonChallenge,
      completeSSOExchange,
      establishSession,
      completeStepUp,
      confirmWithSSO,
      refreshSession,
      reloadCurrentUser,
      refreshFeatureFlags,
      withFreshAccessToken,
      logout,
      logoutAll,
      enrollPasskey,
      listPasskeys,
      renamePasskey,
      deletePasskey,
    }),
    [
      abandonChallenge,
      accessToken,
      capabilities,
      completeSSOExchange,
      completeRecovery,
      completeStepUp,
      completeTOTP,
      confirmWithSSO,
      deletePasskey,
      enrollPasskey,
      error,
      establishSession,
      featureFlags,
      listPasskeys,
      logout,
      logoutAll,
      pendingChallenge,
      policy,
      refreshFeatureFlags,
      refreshSession,
      reloadCurrentUser,
      renamePasskey,
      session,
      signInWithPasskey,
      startPasswordLogin,
      status,
      stepUpMethods,
      user,
      withFreshAccessToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function challengeToken(challenge: PendingChallenge, method: ChallengeMethod) {
  return method === "totp"
    ? challenge.totpChallengeToken
    : challenge.recoveryChallengeToken;
}

/** Why a sign-in attempt cannot go on, and what the person can still do. */
function endedChallengeMessage(
  status: number,
  method: ChallengeMethod,
  challenge: PendingChallenge,
) {
  if (status === 429) {
    return "Too many attempts. Wait a few minutes, then start over.";
  }
  if (Date.parse(challenge.expiresAt) <= Date.now()) {
    return "This sign-in attempt expired. Start over to sign in again.";
  }
  if (status >= 500) {
    return "Starling couldn't check that code, so this sign-in attempt has ended. Start over to try again.";
  }
  if (method === "recovery") {
    return "That recovery code wasn't accepted, and each sign-in attempt takes only one code. Start over to try again.";
  }
  return challenge.spent.recovery
    ? "That code wasn't accepted, and each sign-in attempt takes only one code. Start over to try again."
    : "That code wasn't accepted, and each sign-in attempt takes only one code. Start over, or use a recovery code instead.";
}

/** True when the session's step-up time has moved past what it was. */
function steppedUpSince(now: string | undefined, before: string | null) {
  if (now === undefined) return false;
  return before === null || Date.parse(now) > Date.parse(before);
}

/** The auth context when one is mounted: for shared components (a step-up
 * dialog) that also render in tests and previews without a provider. */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

/**
 * Whether two flag snapshots say the same thing. A re-read that found nothing
 * new must not replace the snapshot, or every consumer of it re-renders on a
 * poll that changed nothing.
 */
function sameFeatureFlags(
  left: Record<string, boolean> | null,
  right: Record<string, boolean> | null,
) {
  if (left === right) return true;
  if (!left || !right) return false;
  const keys = Object.keys(left);
  return (
    keys.length === Object.keys(right).length &&
    keys.every((key) => left[key] === right[key])
  );
}

function errorMessage(err: unknown) {
  return err instanceof Error ? err.message : "Authentication request failed";
}

function isUnauthorizedError(err: unknown) {
  return statusOf(err) === 401;
}

function statusOf(err: unknown) {
  if (err instanceof AuthAPIError) return err.status;
  if (typeof err === "object" && err !== null && "status" in err) {
    const status = (err as { status?: unknown }).status;
    return typeof status === "number" ? status : undefined;
  }
  return undefined;
}

function readStoredRefreshToken() {
  if (typeof window === "undefined") return null;
  const durable = window.localStorage.getItem(refreshTokenStorageKey);
  if (durable) {
    window.sessionStorage.setItem(refreshTokenStorageKey, durable);
    return durable;
  }
  const session = window.sessionStorage.getItem(refreshTokenStorageKey);
  if (session) {
    window.localStorage.setItem(refreshTokenStorageKey, session);
  }
  return session;
}

function writeStoredRefreshToken(token: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(refreshTokenStorageKey, token);
  window.sessionStorage.setItem(refreshTokenStorageKey, token);
}

function clearStoredRefreshToken() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(refreshTokenStorageKey);
  window.sessionStorage.removeItem(refreshTokenStorageKey);
}
